import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Trash2, Instagram, Unlink } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export function Account() {
  const { t } = useT();
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState(user?.email || "");
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");

  const { data: account } = useQuery({ queryKey: ["ig-account"], queryFn: async () => (await api.get("/instagram/account")).data });

  const updateEmail = async () => {
    try { await api.put("/auth/update-email", { email }); await refresh(); toast.success(t("account.saved")); }
    catch (e) { toast.error(e.response?.data?.detail || t("errors.generic")); }
  };
  const changePw = async () => {
    try { await api.put("/auth/change-password", { old_password: oldPw, new_password: newPw }); setOldPw(""); setNewPw(""); toast.success(t("account.saved")); }
    catch (e) { toast.error(e.response?.data?.detail || t("errors.generic")); }
  };
  const exportData = async () => {
    const res = await api.get("/account/export");
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "412level-dati.json"; a.click();
    toast.success("Dati esportati");
  };
  const disconnect = async () => { await api.post("/instagram/disconnect"); qc.invalidateQueries(); toast.success("Instagram scollegato"); };
  const deleteAcc = async () => { await api.delete("/auth/account"); await logout(); navigate("/"); };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("account.title")}</h1>
        </div>
        <Tabs defaultValue="profile">
          <TabsList className="rounded-xl">
            <TabsTrigger value="profile" data-testid="tab-profile">{t("account.profile")}</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">{t("account.security")}</TabsTrigger>
            <TabsTrigger value="privacy" data-testid="tab-privacy">{t("account.dataPrivacy")}</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-5 space-y-5">
            <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
              <div className="space-y-1.5"><Label>{t("account.name")}</Label><Input value={user?.name || ""} disabled className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>{t("account.email")}</Label>
                <div className="flex gap-2">
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-email" className="rounded-xl" />
                  <Button onClick={updateEmail} data-testid="update-email-btn" className="rounded-xl shrink-0">{t("account.updateEmail")}</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>{t("account.language")}</Label><Input value="Italiano" disabled className="rounded-xl" /></div>
                <div className="space-y-1.5"><Label>{t("account.timezone")}</Label><Input value={user?.timezone || "Europe/Rome"} disabled className="rounded-xl" /></div>
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <Label>{t("account.igConnected")}</Label>
              {account ? (
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-orange-400"><Instagram className="h-5 w-5 text-white" /></div>
                    <div><div className="font-medium">@{account.username}</div><div className="text-sm text-muted-foreground">{account.followers?.toLocaleString("it-IT")} follower</div></div>
                  </div>
                  <Button variant="outline" size="sm" onClick={disconnect} data-testid="disconnect-ig-btn" className="rounded-xl"><Unlink className="mr-1.5 h-4 w-4" />{t("account.disconnect")}</Button>
                </div>
              ) : <p className="mt-2 text-sm text-muted-foreground">{t("dashboard.noAccountDesc")}</p>}
            </div>
          </TabsContent>

          <TabsContent value="security" className="mt-5">
            <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
              <div className="space-y-1.5"><Label>{t("account.currentPassword")}</Label><Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} data-testid="old-password" className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>{t("account.newPassword")}</Label><Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password" className="rounded-xl" /></div>
              <Button onClick={changePw} disabled={!oldPw || !newPw} data-testid="change-password-btn" className="rounded-xl">{t("account.changePassword")}</Button>
            </div>
          </TabsContent>

          <TabsContent value="privacy" className="mt-5 space-y-5">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-semibold">{t("account.exportData")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">Scarica una copia completa dei tuoi dati in formato JSON (GDPR).</p>
              <Button onClick={exportData} variant="outline" data-testid="export-data-btn" className="mt-4 rounded-xl"><Download className="mr-2 h-4 w-4" />{t("account.exportData")}</Button>
            </div>
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-6">
              <h3 className="font-semibold text-rose-600 dark:text-rose-400">{t("account.deleteAccount")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t("account.deleteConfirm")}</p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" data-testid="delete-account-btn" className="mt-4 rounded-xl"><Trash2 className="mr-2 h-4 w-4" />{t("account.deleteAccount")}</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>{t("account.deleteAccount")}</AlertDialogTitle><AlertDialogDescription>{t("account.deleteConfirm")}</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cancel-delete-btn" className="rounded-xl">{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={deleteAcc} data-testid="confirm-delete-btn"
                      className="rounded-xl bg-rose-600 text-white shadow-sm hover:bg-rose-700 focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 disabled:bg-rose-600/50 disabled:text-white/80 dark:bg-rose-600 dark:text-white dark:hover:bg-rose-500">
                      <Trash2 className="mr-2 h-4 w-4" />{t("account.deleteConfirmCta")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
